import React, { useMemo } from "react";
import { FiPackage, FiTrendingDown, FiTrendingUp, FiAlertCircle } from "react-icons/fi";
import { useGetPOSAvailableStockQuery } from "../services/api";

function formatNumber(value) {
  return new Intl.NumberFormat("en-ET", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(Number(value) || 0);
}

export default function StockOverviewPage() {
  const { data: stockData, isLoading } = useGetPOSAvailableStockQuery();
  const stockItems = useMemo(() => stockData?.data || [], [stockData]);

  // Group stock by origin
  const stockByOrigin = useMemo(() => {
    return stockItems.reduce((summary, stock) => {
      const origin = stock.origin || 'የሀገር ውስጥ';
      if (!summary[origin]) {
        summary[origin] = {
          items: [],
          totalGrams: 0,
          initialGrams: 0,
          materials: new Set(),
        };
      }
      
      const availableGrams = Number(stock.available_quantity || 0);
      const initialGrams = Number(stock.quantity || 0);
      
      summary[origin].items.push(stock);
      summary[origin].totalGrams += availableGrams;
      summary[origin].initialGrams += initialGrams;
      summary[origin].materials.add(stock.material_type);
      
      return summary;
    }, {});
  }, [stockItems]);

  // Group stock by material type
  const stockByMaterial = useMemo(() => {
    return stockItems.reduce((summary, stock) => {
      const material = stock.material_type || 'Unknown';
      if (!summary[material]) {
        summary[material] = {
          items: [],
          totalGrams: 0,
          initialGrams: 0,
          origins: new Set(),
        };
      }
      
      const availableGrams = Number(stock.available_quantity || 0);
      const initialGrams = Number(stock.quantity || 0);
      
      summary[material].items.push(stock);
      summary[material].totalGrams += availableGrams;
      summary[material].initialGrams += initialGrams;
      summary[material].origins.add(stock.origin || 'የሀገር ውስጥ');
      
      return summary;
    }, {});
  }, [stockItems]);

  // Overall totals
  const overallTotal = useMemo(() => {
    return stockItems.reduce((totals, stock) => {
      return {
        totalGrams: totals.totalGrams + Number(stock.available_quantity || 0),
        initialGrams: totals.initialGrams + Number(stock.quantity || 0),
        itemCount: totals.itemCount + 1,
      };
    }, { totalGrams: 0, initialGrams: 0, itemCount: 0 });
  }, [stockItems]);

  const overallPercentage = overallTotal.initialGrams > 0 
    ? (overallTotal.totalGrams / overallTotal.initialGrams) * 100 
    : 0;

  if (isLoading) {
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        background: "#F7F8FA",
      }}>
        <div className="premium-spinner" />
      </div>
    );
  }

  return (
    <div style={{
      background: "#F7F8FA",
      minHeight: "100vh",
      padding: "24px",
    }}>
      {/* Page Header */}
      <div style={{ marginBottom: "24px" }}>
        <h1 style={{ fontSize: "2rem", fontWeight: 700, margin: 0, marginBottom: "8px", color: "#14213D" }}>
          📊 Stock Overview
        </h1>
        <p style={{ fontSize: "0.9375rem", color: "#64748B", margin: 0 }}>
          Complete inventory breakdown by origin and material type
        </p>
      </div>

      {/* Overall Summary Card */}
      <div style={{
        background: "white",
        borderRadius: "16px",
        border: "2px solid #E2E8F0",
        padding: "32px",
        marginBottom: "24px",
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
      }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "32px",
        }}>
          {/* Total Available */}
          <div>
            <div style={{
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "#64748B",
              marginBottom: "8px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}>
              Total Available
            </div>
            <div style={{
              fontSize: "2.5rem",
              fontWeight: 800,
              color: "#F5B82E",
              lineHeight: 1,
              marginBottom: "4px",
            }}>
              {formatNumber(overallTotal.totalGrams)}
              <span style={{ fontSize: "1.25rem", marginLeft: "8px", color: "#94A3B8" }}>g</span>
            </div>
          </div>

          {/* Initial Stock */}
          <div>
            <div style={{
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "#64748B",
              marginBottom: "8px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}>
              Initial Stock
            </div>
            <div style={{
              fontSize: "2.5rem",
              fontWeight: 800,
              color: "#14213D",
              lineHeight: 1,
              marginBottom: "4px",
            }}>
              {formatNumber(overallTotal.initialGrams)}
              <span style={{ fontSize: "1.25rem", marginLeft: "8px", color: "#94A3B8" }}>g</span>
            </div>
          </div>

          {/* Stock Level */}
          <div>
            <div style={{
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "#64748B",
              marginBottom: "8px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}>
              Stock Level
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div style={{
                fontSize: "2.5rem",
                fontWeight: 800,
                color: overallPercentage < 30 ? "#DC2626" : overallPercentage < 60 ? "#F59E0B" : "#10B981",
                lineHeight: 1,
              }}>
                {formatNumber(overallPercentage)}
                <span style={{ fontSize: "1.25rem", marginLeft: "4px" }}>%</span>
              </div>
              {overallPercentage < 30 ? (
                <FiTrendingDown size={32} style={{ color: "#DC2626" }} />
              ) : (
                <FiTrendingUp size={32} style={{ color: "#10B981" }} />
              )}
            </div>
          </div>

          {/* Total Items */}
          <div>
            <div style={{
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "#64748B",
              marginBottom: "8px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}>
              Stock Items
            </div>
            <div style={{
              fontSize: "2.5rem",
              fontWeight: 800,
              color: "#3B82F6",
              lineHeight: 1,
              marginBottom: "4px",
            }}>
              {overallTotal.itemCount}
            </div>
          </div>
        </div>
      </div>

      {/* Stock by Origin */}
      <div style={{ marginBottom: "32px" }}>
        <h2 style={{
          fontSize: "1.5rem",
          fontWeight: 700,
          color: "#14213D",
          marginBottom: "16px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
        }}>
          🌍 Stock by Origin
        </h2>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
          gap: "16px",
        }}>
          {Object.entries(stockByOrigin).map(([origin, data]) => {
            const percentage = data.initialGrams > 0 
              ? (data.totalGrams / data.initialGrams) * 100 
              : 0;
            const isLow = percentage < 30;
            const isMedium = percentage >= 30 && percentage < 60;

            return (
              <div
                key={origin}
                style={{
                  background: "white",
                  borderRadius: "12px",
                  border: "2px solid #E2E8F0",
                  padding: "24px",
                }}
              >
                {/* Header */}
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "20px",
                }}>
                  <div style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "12px",
                    background: origin === 'የሀገር ውስጥ' 
                      ? "linear-gradient(135deg, #10B981 0%, #059669 100%)"
                      : "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.5rem",
                    flexShrink: 0,
                  }}>
                    {origin === 'የሀገር ውስጥ' ? '🏠' : '🌍'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: "1.125rem",
                      fontWeight: 700,
                      color: "#14213D",
                      marginBottom: "2px",
                    }}>
                      {origin}
                    </div>
                    <div style={{
                      fontSize: "0.75rem",
                      color: "#94A3B8",
                    }}>
                      {data.items.length} items • {data.materials.size} materials
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "16px",
                  marginBottom: "16px",
                }}>
                  <div>
                    <div style={{ fontSize: "0.75rem", color: "#64748B", marginBottom: "4px", fontWeight: 600 }}>
                      Available
                    </div>
                    <div style={{
                      fontSize: "1.5rem",
                      fontWeight: 800,
                      color: isLow ? "#DC2626" : isMedium ? "#F59E0B" : "#10B981",
                    }}>
                      {formatNumber(data.totalGrams)}g
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: "0.75rem", color: "#64748B", marginBottom: "4px", fontWeight: 600 }}>
                      Initial
                    </div>
                    <div style={{
                      fontSize: "1.5rem",
                      fontWeight: 800,
                      color: "#14213D",
                    }}>
                      {formatNumber(data.initialGrams)}g
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div style={{
                  width: "100%",
                  height: "8px",
                  background: "#E2E8F0",
                  borderRadius: "4px",
                  overflow: "hidden",
                  marginBottom: "8px",
                }}>
                  <div style={{
                    width: `${Math.min(percentage, 100)}%`,
                    height: "100%",
                    background: isLow 
                      ? "linear-gradient(90deg, #DC2626 0%, #EF4444 100%)"
                      : isMedium 
                        ? "linear-gradient(90deg, #F59E0B 0%, #FBBF24 100%)"
                        : "linear-gradient(90deg, #10B981 0%, #34D399 100%)",
                    transition: "width 0.3s ease",
                  }} />
                </div>

                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "0.8125rem",
                  color: "#64748B",
                }}>
                  <span>{formatNumber(percentage)}% remaining</span>
                  {isLow && (
                    <span style={{ color: "#DC2626", fontWeight: 600, display: "flex", alignItems: "center", gap: "4px" }}>
                      <FiAlertCircle size={14} /> Low Stock
                    </span>
                  )}
                </div>

                {/* Materials List */}
                <div style={{
                  marginTop: "16px",
                  paddingTop: "16px",
                  borderTop: "1px solid #F1F5F9",
                }}>
                  <div style={{ fontSize: "0.75rem", color: "#64748B", marginBottom: "8px", fontWeight: 600 }}>
                    Materials
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    {Array.from(data.materials).map((material) => (
                      <span
                        key={material}
                        style={{
                          padding: "4px 10px",
                          background: "#F1F5F9",
                          borderRadius: "12px",
                          fontSize: "0.6875rem",
                          fontWeight: 600,
                          color: "#475569",
                        }}
                      >
                        {material}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Stock by Material Type */}
      <div style={{ marginBottom: "32px" }}>
        <h2 style={{
          fontSize: "1.5rem",
          fontWeight: 700,
          color: "#14213D",
          marginBottom: "16px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
        }}>
          💎 Stock by Material Type
        </h2>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          gap: "16px",
        }}>
          {Object.entries(stockByMaterial).map(([material, data]) => {
            const percentage = data.initialGrams > 0 
              ? (data.totalGrams / data.initialGrams) * 100 
              : 0;
            const isLow = percentage < 30;
            const isMedium = percentage >= 30 && percentage < 60;

            return (
              <div
                key={material}
                style={{
                  background: "white",
                  borderRadius: "12px",
                  border: "2px solid #E2E8F0",
                  padding: "20px",
                }}
              >
                {/* Header */}
                <div style={{
                  display: "flex",
                  alignItems: "start",
                  justifyContent: "space-between",
                  marginBottom: "16px",
                }}>
                  <div>
                    <div style={{
                      fontSize: "1rem",
                      fontWeight: 700,
                      color: "#14213D",
                      marginBottom: "4px",
                    }}>
                      {material}
                    </div>
                    <div style={{
                      fontSize: "0.75rem",
                      color: "#94A3B8",
                    }}>
                      {data.items.length} items
                    </div>
                  </div>
                  <div style={{
                    padding: "6px 12px",
                    background: isLow ? "#FEE2E2" : isMedium ? "#FEF3C7" : "#D1FAE5",
                    color: isLow ? "#DC2626" : isMedium ? "#92400E" : "#047857",
                    borderRadius: "16px",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                  }}>
                    {formatNumber(percentage)}%
                  </div>
                </div>

                {/* Stock Numbers */}
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "12px",
                }}>
                  <div>
                    <div style={{ fontSize: "0.6875rem", color: "#64748B", marginBottom: "4px" }}>
                      Available
                    </div>
                    <div style={{
                      fontSize: "1.25rem",
                      fontWeight: 800,
                      color: "#F5B82E",
                    }}>
                      {formatNumber(data.totalGrams)}g
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "0.6875rem", color: "#64748B", marginBottom: "4px" }}>
                      Initial
                    </div>
                    <div style={{
                      fontSize: "1.25rem",
                      fontWeight: 800,
                      color: "#14213D",
                    }}>
                      {formatNumber(data.initialGrams)}g
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div style={{
                  width: "100%",
                  height: "6px",
                  background: "#E2E8F0",
                  borderRadius: "3px",
                  overflow: "hidden",
                  marginBottom: "12px",
                }}>
                  <div style={{
                    width: `${Math.min(percentage, 100)}%`,
                    height: "100%",
                    background: isLow 
                      ? "linear-gradient(90deg, #DC2626 0%, #EF4444 100%)"
                      : isMedium 
                        ? "linear-gradient(90deg, #F59E0B 0%, #FBBF24 100%)"
                        : "linear-gradient(90deg, #10B981 0%, #34D399 100%)",
                    transition: "width 0.3s ease",
                  }} />
                </div>

                {/* Origins */}
                <div style={{
                  paddingTop: "12px",
                  borderTop: "1px solid #F1F5F9",
                }}>
                  <div style={{ fontSize: "0.6875rem", color: "#64748B", marginBottom: "6px" }}>
                    Origins
                  </div>
                  <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                    {Array.from(data.origins).map((origin) => (
                      <span
                        key={origin}
                        style={{
                          padding: "3px 8px",
                          background: origin === 'የሀገር ውስጥ' ? "#D1FAE5" : "#DBEAFE",
                          color: origin === 'የሀገር ውስጥ' ? "#047857" : "#1E40AF",
                          borderRadius: "10px",
                          fontSize: "0.625rem",
                          fontWeight: 600,
                        }}
                      >
                        {origin === 'የሀገር ውስጥ' ? '🏠' : '🌍'} {origin}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* All Stock Items - Detailed Table */}
      <div>
        <h2 style={{
          fontSize: "1.5rem",
          fontWeight: 700,
          color: "#14213D",
          marginBottom: "16px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
        }}>
          📋 All Stock Items
        </h2>

        <div style={{
          background: "white",
          borderRadius: "12px",
          border: "2px solid #E2E8F0",
          overflow: "hidden",
        }}>
          {/* Table */}
          <div style={{ overflowX: "auto" }}>
            <table style={{
              width: "100%",
              borderCollapse: "collapse",
            }}>
              <thead>
                <tr style={{ background: "#F9FAFB", borderBottom: "2px solid #E2E8F0" }}>
                  <th style={{
                    padding: "16px",
                    textAlign: "left",
                    fontSize: "0.8125rem",
                    fontWeight: 700,
                    color: "#14213D",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}>
                    #
                  </th>
                  <th style={{
                    padding: "16px",
                    textAlign: "left",
                    fontSize: "0.8125rem",
                    fontWeight: 700,
                    color: "#14213D",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}>
                    Stock Name
                  </th>
                  <th style={{
                    padding: "16px",
                    textAlign: "left",
                    fontSize: "0.8125rem",
                    fontWeight: 700,
                    color: "#14213D",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}>
                    Material
                  </th>
                  <th style={{
                    padding: "16px",
                    textAlign: "left",
                    fontSize: "0.8125rem",
                    fontWeight: 700,
                    color: "#14213D",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}>
                    Origin
                  </th>
                  <th style={{
                    padding: "16px",
                    textAlign: "right",
                    fontSize: "0.8125rem",
                    fontWeight: 700,
                    color: "#14213D",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}>
                    Available
                  </th>
                  <th style={{
                    padding: "16px",
                    textAlign: "right",
                    fontSize: "0.8125rem",
                    fontWeight: 700,
                    color: "#14213D",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}>
                    Initial
                  </th>
                  <th style={{
                    padding: "16px",
                    textAlign: "center",
                    fontSize: "0.8125rem",
                    fontWeight: 700,
                    color: "#14213D",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}>
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {stockItems.map((item, index) => {
                  const percentage = item.quantity > 0 
                    ? (Number(item.available_quantity) / Number(item.quantity)) * 100 
                    : 0;
                  const isLow = percentage < 30;
                  const isMedium = percentage >= 30 && percentage < 60;

                  return (
                    <tr
                      key={item.id}
                      style={{
                        borderBottom: "1px solid #F1F5F9",
                        transition: "background 0.2s",
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "#F9FAFB"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                    >
                      <td style={{
                        padding: "16px",
                        fontSize: "0.875rem",
                        color: "#64748B",
                        fontWeight: 600,
                      }}>
                        {index + 1}
                      </td>
                      <td style={{
                        padding: "16px",
                        fontSize: "0.9375rem",
                        color: "#14213D",
                        fontWeight: 600,
                      }}>
                        {item.name || `Stock #${item.id}`}
                      </td>
                      <td style={{
                        padding: "16px",
                      }}>
                        <span style={{
                          padding: "4px 10px",
                          background: "#F1F5F9",
                          borderRadius: "12px",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          color: "#475569",
                          display: "inline-block",
                        }}>
                          {item.material_type}
                        </span>
                      </td>
                      <td style={{
                        padding: "16px",
                      }}>
                        <span style={{
                          padding: "4px 10px",
                          background: (item.origin || 'የሀገር ውስጥ') === 'የሀገር ውስጥ' ? "#D1FAE5" : "#DBEAFE",
                          color: (item.origin || 'የሀገር ውስጥ') === 'የሀገር ውስጥ' ? "#047857" : "#1E40AF",
                          borderRadius: "12px",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                        }}>
                          {(item.origin || 'የሀገር ውስጥ') === 'የሀገር ውስጥ' ? '🏠' : '🌍'}
                          {item.origin || 'የሀገር ውስጥ'}
                        </span>
                      </td>
                      <td style={{
                        padding: "16px",
                        textAlign: "right",
                        fontSize: "1rem",
                        fontWeight: 700,
                        color: isLow ? "#DC2626" : isMedium ? "#F59E0B" : "#10B981",
                      }}>
                        {formatNumber(item.available_quantity)} g
                      </td>
                      <td style={{
                        padding: "16px",
                        textAlign: "right",
                        fontSize: "0.9375rem",
                        fontWeight: 600,
                        color: "#64748B",
                      }}>
                        {formatNumber(item.quantity)} g
                      </td>
                      <td style={{
                        padding: "16px",
                        textAlign: "center",
                      }}>
                        <div style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "6px 12px",
                          background: isLow ? "#FEE2E2" : isMedium ? "#FEF3C7" : "#D1FAE5",
                          color: isLow ? "#DC2626" : isMedium ? "#92400E" : "#047857",
                          borderRadius: "16px",
                          fontSize: "0.75rem",
                          fontWeight: 700,
                        }}>
                          {isLow && <FiAlertCircle size={14} />}
                          {formatNumber(percentage)}%
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Table Footer Summary */}
          <div style={{
            padding: "16px 24px",
            background: "#F9FAFB",
            borderTop: "2px solid #E2E8F0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
            <div style={{
              fontSize: "0.875rem",
              color: "#64748B",
              fontWeight: 600,
            }}>
              Total: {stockItems.length} stock items
            </div>
            <div style={{
              display: "flex",
              gap: "32px",
            }}>
              <div>
                <span style={{ fontSize: "0.75rem", color: "#64748B", marginRight: "8px" }}>
                  Total Available:
                </span>
                <span style={{ fontSize: "1rem", fontWeight: 800, color: "#F5B82E" }}>
                  {formatNumber(overallTotal.totalGrams)}g
                </span>
              </div>
              <div>
                <span style={{ fontSize: "0.75rem", color: "#64748B", marginRight: "8px" }}>
                  Total Initial:
                </span>
                <span style={{ fontSize: "1rem", fontWeight: 800, color: "#14213D" }}>
                  {formatNumber(overallTotal.initialGrams)}g
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
